import React, {lazy, useEffect} from 'react';
import {Navigate, Outlet, Route, Routes} from 'react-router-dom';
import {useLocale} from "./Providers/LocaleProvider";
import {useTheme} from "./Providers/Theme";
import {AuthOnly, GuestOnly, useAuth} from "./Providers/Auth";
import Dashboard from './components/Dashboard';
import {NetworkStatus} from "./components/NetworkStatus";
import {LazyLoadingSuspense} from "./components/LazyLoadingSuspense";
import useMeta from "./hooks/Api/useMeta";
import {SWRConfig} from "swr";
import {isApiError} from "./hooks/useApiFetchV2";

const Login = lazy(() => import('./views/Login'))
const Home = lazy(() => import('./views/Home'))
const Settings = lazy(() => import('./views/Settings'))
const Categories = lazy(() => import('./views/Categories'))
const ServiceProviders = lazy(() => import('./views/ServiceProviders'))
const EditServiceProvider = lazy(() => import('./views/ServiceProviders/EditServiceProvider'))

const Users = lazy(() => import('./views/Users'))
const EditUser = lazy(() => import('./views/Users/EditUser'))

const SendNotification = lazy(() => import('./views/Notifications/SendNotification'))

const Banners = lazy(() => import('./views/Banners'))
const Transactions = lazy(() => import('./views/Transactions'))
const CardStatistics = lazy(() => import('./views/CardStatistics'))

const Sales = lazy(() => import('./views/Sales'))
const SalesRecords = lazy(() => import('./views/SalesRecords'))
const SalesStatistics = lazy(() => import('./views/SalesStatistics'))


const Purchases = lazy(() => import('./views/Purchases'))
const PurchasesRecords = lazy(() => import('./views/PurchasesRecords'))
const PurchasesStatistics = lazy(() => import('./views/PurchasesStatistics'))

const Employees = lazy(() => import('./views/Employees'))
const Salaries = lazy(() => import('./views/Salaries'))

const Expenses = lazy(() => import('./views/Expenses'))

function App() {

    const {locale} = useLocale();
    const {setThemeOptions} = useTheme();
    const {setToken} = useAuth();

    const {data: meta} = useMeta()

    useEffect(() => {
        setThemeOptions(o => ({...o, direction: locale === 'ar' ? 'rtl' : 'ltr'}))
    }, [locale])


    return (
        <>
            <SWRConfig
                value={{
                    onError(err) {
                        if (isApiError(err)) {
                            if (err.error.type === "Unauthenticated") {
                                setToken('')
                            }
                        }
                    }
                }}
            >
                {/*<NetworkStatus/>*/}
                <React.Suspense fallback={<LazyLoadingSuspense/>}>
                    <Routes>
                        <Route element={<GuestOnly children={<Outlet/>} otherwise={<Navigate to={"/"}/>}/>}>
                            <Route path={"/login"} element={<Login/>}/>
                        </Route>
                        <Route
                            path={"/"}
                            element={
                                <AuthOnly children={<Dashboard/>} otherwise={<Navigate to={"/login"}/>}/>
                            }>
                            <Route path={"/"} element={<Home/>}/>
                            <Route path={"/banners"} element={<Banners key={"/banners"}/>}/>
                            <Route path={"/users"} element={<Users key={"/users"}/>}/>
                            <Route path={"/users/:id"} element={<EditUser key={"/users/:id"}/>}/>
                            {/*<Route path={"/categories"} element={<Categories parentId={meta?.service_providers_super_parent_id} key={"/categories"}/>}/>*/}
                            <Route path={"/service_providers_categories"} 
                                   element={<Categories path={'/service_providers_categories'} localeBase={'service_providers_categories'} 
                                       parentId={meta?.service_providers_super_parent_id}
                                       key={"/service_providers_categories"}/>}/>
                            <Route path={"/cards_categories"}
                                   element={<Categories path={'/cards_categories'} localeBase={'cards_categories'}
                                                        parentId={meta?.cards_super_parent_id}
                                                        key={"/cards_categories"}/>}/>
                            <Route path={"/service_providers"}
                                   element={<ServiceProviders key={"/service_providers"}/>}/>
                            <Route path={"/service_providers/:id"}
                                   element={<EditServiceProvider key={"/service_providers/:id"}/>}/>
                            <Route path={"/notifications"} element={<SendNotification key={"/notifications"}/>}/>
                            <Route path={"/transactions"} element={<Transactions key={"/transactions"}/>}/>
                            <Route path={"/cards_statistics"} element={<CardStatistics key={"/cards_statistics"}/>}/>
                            <Route path={"/settings"} element={<Settings/>}/>

                            <Route path={"/sales"} element={<Sales key={"/sales"}/>}/>
                            <Route path={"/sales/records"} element={<SalesRecords key={"/sales/records"}/>}/>
                            <Route path={"/sales/statistics"} element={<SalesStatistics key={"/sales/statistics"}/>}/>

                            <Route path={"/purchases"} element={<Purchases key={"/purchases"}/>}/>
                            <Route path={"/purchases/records"}
                                   element={<PurchasesRecords key={"/purchases/records"}/>}/>
                            <Route path={"/purchases/statistics"}
                                   element={<PurchasesStatistics key={"/purchases/statistics"}/>}/>

                            <Route path={"/employees"} element={<Employees key={"/employees"}/>}/>
                            <Route path={"/employees/salaries"} element={<Salaries key={"/employees/salaries"}/>}/>

                            <Route path={"/expenses"} element={<Expenses key={"/expenses"}/>}/>
                        </Route>
                    </Routes>
                </React.Suspense>
            </SWRConfig>
        </>
    );
}

export default App;
